import { Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceDefaultApproversSchema } from "@msuite/utils/schemas";
import { getFullName } from "@msuite/utils/users";
import { useFormContext } from "react-hook-form";
import { useIncomingInvoiceApproverCandidates } from "@/hooks/use-incoming-invoice-approver-candidates";
import { useMembers } from "@/hooks/use-members";

/** Props Interface */
interface IncomingInvoiceDefaultApproversFormProps {
	isOrganizationSet: boolean;
}

export function IncomingInvoiceDefaultApproversForm({
	isOrganizationSet,
}: IncomingInvoiceDefaultApproversFormProps) {
	/** Context */
	const methods = useFormContext<IncomingInvoiceDefaultApproversSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const { members } = useMembers();
	const { candidateIds } = useIncomingInvoiceApproverCandidates();

	/** Computed */
	// Only active members who may read incoming invoices: the server refuses anybody else, because
	// an offboarded person, or one whose role does not open the invoice, could not approve it.
	const options = members
		.filter((member) => member.membership.state === "active")
		.filter((member) => candidateIds.has(member.user.id))
		.map((member) => ({
			value: member.user.id,
			label: getFullName(member.user),
			item: member.user,
		}));

	/** Render */
	return (
		<Select
			name="userIds"
			control={methods.control}
			label={t("incoming_invoices.default_approvers")}
			isRequired={isOrganizationSet}
			multiple
			multipleSelectionRender={(items) => (
				<p className="whitespace-pre text-left">{items.map((item) => item.label).join("\n")}</p>
			)}
			options={options}
			itemNotFoundLabel={t("placeholders.select_member")}
			helperText={t("incoming_invoices.approvers_need_read_rule")}
			data-cy="incoming-invoice-default-approvers-select"
		/>
	);
}
